import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Settings, FileText, Key, Variable as VariableIcon } from 'lucide-react';
import { useStore, Collection, Variable, CollectionAuth } from '../../stores';
import { VariableEditor } from '../environments';
import { OAuth2Auth, HawkAuth } from '../request-builder/auth';
import { cn } from '../../lib/utils';

interface CollectionSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collection: Collection;
}

type TabType = 'general' | 'variables' | 'authorization';

export function CollectionSettingsDialog({
  open,
  onOpenChange,
  collection,
}: CollectionSettingsDialogProps) {
  const { updateCollection } = useStore();
  const [activeTab, setActiveTab] = useState<TabType>('general');

  // Form state
  const [name, setName] = useState(collection.name);
  const [description, setDescription] = useState(collection.description || '');
  const [variables, setVariables] = useState<Variable[]>(collection.variables || []);
  const [auth, setAuth] = useState<CollectionAuth>(collection.auth || { type: 'none' });

  // Reset form when collection changes or dialog opens
  useEffect(() => {
    if (open) {
      setName(collection.name);
      setDescription(collection.description || '');
      setVariables(collection.variables || []);
      setAuth(collection.auth || { type: 'none' });
      setActiveTab('general');
    }
  }, [open, collection]);

  const handleSave = () => {
    if (!name.trim()) return;

    updateCollection(collection.id, {
      name: name.trim(),
      description: description.trim() || undefined,
      variables: variables.length > 0 ? variables : undefined,
      auth: auth.type !== 'none' ? auth : undefined,
    });

    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.metaKey) {
      handleSave();
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'general', label: 'General', icon: <FileText className="w-4 h-4" /> },
    { id: 'variables', label: 'Variables', icon: <VariableIcon className="w-4 h-4" /> },
    { id: 'authorization', label: 'Authorization', icon: <Key className="w-4 h-4" /> },
  ];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg shadow-xl z-50 w-[600px] max-h-[85vh] overflow-hidden flex flex-col"
          onKeyDown={handleKeyDown}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Settings className="w-5 h-5 text-yellow-500" />
              </div>
              <Dialog.Title className="text-lg font-semibold">
                Collection Settings
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="p-1 rounded hover:bg-accent transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </Dialog.Close>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-border px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.id
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.icon}
                {tab.label}
                {tab.id === 'variables' && variables.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-muted rounded">
                    {variables.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'general' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="collection-name" className="text-sm font-medium text-foreground">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="collection-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="My Collection"
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                    autoFocus
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="collection-description" className="text-sm font-medium text-foreground">
                    Description
                  </label>
                  <textarea
                    id="collection-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional description for this collection..."
                    rows={4}
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
                  />
                </div>
              </div>
            )}

            {activeTab === 'variables' && (
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    Collection variables are available to all requests in this collection.
                    Use <code className="px-1 py-0.5 bg-muted rounded text-xs">{'{{variableName}}'}</code> syntax.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Priority: Request &gt; Collection &gt; Environment &gt; Global
                  </p>
                </div>
                <VariableEditor
                  variables={variables}
                  onChange={setVariables}
                />
              </div>
            )}

            {activeTab === 'authorization' && (
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    Set default authorization for all requests in this collection.
                    Requests can inherit this auth or override with their own.
                  </p>
                </div>

                <select
                  value={auth.type}
                  onChange={(e) => setAuth({ type: e.target.value as CollectionAuth['type'] })}
                  className="px-3 py-2 bg-background border border-input rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="none">No Auth</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Basic Auth</option>
                  <option value="api-key">API Key</option>
                  <option value="oauth2">OAuth 2.0</option>
                  <option value="hawk">Hawk</option>
                </select>

                {auth.type === 'bearer' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Token</label>
                    <input
                      type="text"
                      value={auth.bearer?.token || ''}
                      onChange={(e) =>
                        setAuth({ ...auth, bearer: { token: e.target.value } })
                      }
                      placeholder="Bearer token"
                      className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                )}

                {auth.type === 'basic' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Username</label>
                      <input
                        type="text"
                        value={auth.basic?.username || ''}
                        onChange={(e) =>
                          setAuth({
                            ...auth,
                            basic: {
                              username: e.target.value,
                              password: auth.basic?.password || '',
                            },
                          })
                        }
                        className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Password</label>
                      <input
                        type="password"
                        value={auth.basic?.password || ''}
                        onChange={(e) =>
                          setAuth({
                            ...auth,
                            basic: {
                              username: auth.basic?.username || '',
                              password: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>
                )}

                {auth.type === 'api-key' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Key</label>
                      <input
                        type="text"
                        value={auth.apiKey?.key || ''}
                        onChange={(e) =>
                          setAuth({
                            ...auth,
                            apiKey: {
                              key: e.target.value,
                              value: auth.apiKey?.value || '',
                              addTo: auth.apiKey?.addTo || 'header',
                            },
                          })
                        }
                        className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Value</label>
                      <input
                        type="text"
                        value={auth.apiKey?.value || ''}
                        onChange={(e) =>
                          setAuth({
                            ...auth,
                            apiKey: {
                              key: auth.apiKey?.key || '',
                              value: e.target.value,
                              addTo: auth.apiKey?.addTo || 'header',
                            },
                          })
                        }
                        className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Add to</label>
                      <select
                        value={auth.apiKey?.addTo || 'header'}
                        onChange={(e) =>
                          setAuth({
                            ...auth,
                            apiKey: {
                              key: auth.apiKey?.key || '',
                              value: auth.apiKey?.value || '',
                              addTo: e.target.value as 'header' | 'query',
                            },
                          })
                        }
                        className="px-3 py-2 bg-background border border-input rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="header">Header</option>
                        <option value="query">Query Params</option>
                      </select>
                    </div>
                  </div>
                )}

                {auth.type === 'oauth2' && (
                  <OAuth2Auth
                    config={auth.oauth2 || {
                      grantType: 'client_credentials',
                      accessTokenUrl: '',
                      clientId: '',
                    }}
                    onChange={(oauth2) => setAuth({ ...auth, oauth2 })}
                  />
                )}

                {auth.type === 'hawk' && (
                  <HawkAuth
                    config={auth.hawk || {
                      authId: '',
                      authKey: '',
                      algorithm: 'sha256',
                      includePayloadHash: false,
                    }}
                    onChange={(hawk) => setAuth({ ...auth, hawk })}
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-border">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!name.trim()}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Changes
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
